/* eslint-disable */
import React, { useEffect, useState, useCallback } from "react";
import firebase from "./firebaseConfig";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import API_ENDPOINTS from './config/api';

function ProjectView() {
  const [user, setUser] = useState(null);
  const [project, setProject] = useState(null);
  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const [files, setFiles] = useState([]);
  const [apiKey, setApiKey] = useState(null);
  const [isEmbedding, setIsEmbedding] = useState(false);
  const [projectConfig, setProjectConfig] = useState({
    hasGeminiApiKey: false,
    geminiApiKeyPreview: null,
    systemPrompt: '',
    allowedDomains: []
  });
  const [showConfigForm, setShowConfigForm] = useState(false);
  const [configForm, setConfigForm] = useState({
    geminiApiKey: '',
    systemPrompt: '',
    allowedDomains: []
  });
  const [isUpdatingConfig, setIsUpdatingConfig] = useState(false);
  const [isUpdatingApiKey, setIsUpdatingApiKey] = useState(false);
  const [isUpdatingPrompt, setIsUpdatingPrompt] = useState(false);
  const [isUpdatingDomains, setIsUpdatingDomains] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoadingFiles, setIsLoadingFiles] = useState(true);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const navigate = useNavigate();
  const { projectId } = useParams(); // This is now a slug, not actual ID
  const location = useLocation();

  // Utility function to create URL-friendly slug from project name
  const createSlug = (text) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .trim('-'); // Remove leading/trailing hyphens
  };

  // Get default system prompt
  const getDefaultSystemPrompt = () => {
    return `You are an AI assistant helping with questions about uploaded documents.

Please provide helpful and accurate answers based on the context provided from the documents. If the context doesn't contain relevant information to answer the question, please say so clearly.

Guidelines:
- Be concise but thorough in your responses
- Always base your answers on the provided context
- If you're unsure about something, acknowledge the uncertainty
- Format your responses in a clear, readable manner
- Be helpful and professional in your tone`;
  };

  // Format date helper function
  const formatDate = (dateValue) => {
    if (!dateValue) return 'Unknown';
    
    try {
      let date;
      
      // Handle Firestore timestamp format
      if (dateValue.seconds) {
        date = new Date(dateValue.seconds * 1000);
      } 
      // Handle Firestore timestamp with nanoseconds
      else if (dateValue._seconds) {
        date = new Date(dateValue._seconds * 1000);
      }
      // Handle ISO string or regular date
      else {
        date = new Date(dateValue);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Unknown';
      }
      
      return date.toLocaleDateString();
    } catch (error) {
      return 'Unknown';
    }
  };

  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged(u => {
      if (!u) {
        navigate("/login");
      } else {
        setUser(u);
      }
    });
    return () => unsubscribe();
  }, [navigate, projectId]);

  useEffect(() => {
    // Get project data from location state or fetch from backend
    if (location.state?.project) {
      setProject(location.state.project);
    } else if (user) {
      // Fallback: fetch from backend
      fetchProjectFromBackend();
    }
    
    // Load API key when user is available
    if (user) {
      loadApiKey();
    }
  }, [user, projectId, location.state, navigate]);

  // Load files and config after both project and apiKey are available
  useEffect(() => {
    if (user && project && apiKey) {
      loadProjectFiles();
      loadProjectConfig();
    }
    // Don't call the functions if prerequisites aren't met - let them stay in loading state
  }, [user, project, apiKey]);

  // Track when all initial loading is complete
  useEffect(() => {
    // Only set initial loading to false when we have all required data AND both API calls have completed
    const hasAllRequiredData = user && project && apiKey;
    const allApiCallsComplete = !isLoadingFiles && !isLoadingConfig;
    
    if (hasAllRequiredData && allApiCallsComplete) {
      setIsInitialLoading(false);
    }
  }, [user, project, apiKey, isLoadingFiles, isLoadingConfig]);

  const fetchProjectFromBackend = useCallback(async () => {
    if (!user) return;
    
    try {
      const response = await fetch(API_ENDPOINTS.PROJECTS(user.uid));
      if (response.ok) {
        const data = await response.json();
        // Find project by matching slug (URL param) with project name slug
        const foundProject = data.projects.find(p => createSlug(p.name) === projectId);
        if (foundProject) {
          setProject(foundProject);
        } else {
          navigate("/dashboard");
        }
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      navigate("/dashboard");
    }
  }, [user, projectId, navigate]);

  const loadApiKey = useCallback(async () => {
    if (!user) return;
    
    try {
      const response = await fetch(API_ENDPOINTS.API_KEYS(user.uid));
      if (response.ok) {
        const data = await response.json();
        setApiKey(data.apiKey);
      }
    } catch (error) {
      // Error loading API key - continue without it
    }
  }, [user]);

  const loadProjectFiles = useCallback(async () => {
    if (!project) {
      // Don't set loading to false - keep it in loading state until project is available
      return;
    }
    
    setIsLoadingFiles(true);
    try {
      const response = await fetch(API_ENDPOINTS.FILES(project.id));
      if (response.ok) {
        const data = await response.json();
        setFiles(data.files);
      }
    } catch (error) {
      // Error loading files - continue without them
    } finally {
      setIsLoadingFiles(false);
    }
  }, [project]);

  const loadProjectConfig = useCallback(async () => {
    if (!project || !apiKey) {
      // Don't set loading to false - keep it in loading state until both project and apiKey are available
      return;
    }
    
    setIsLoadingConfig(true);
    try {
      const response = await fetch(API_ENDPOINTS.PROJECT_CONFIG(project.id), {
        headers: {
          'x-api-key': apiKey
        }
      });
      
      if (response.ok) {
        const config = await response.json();
        setProjectConfig(config);
        // Set form values from the loaded config
        const formData = {
          geminiApiKey: config.geminiApiKeyPreview || '' // Show preview if available
          , systemPrompt: config.systemPrompt || getDefaultSystemPrompt()
          , allowedDomains: Array.isArray(config.allowedDomains) ? config.allowedDomains : []
        };
        setConfigForm(formData);
      }
    } catch (error) {
      // Error loading project config - continue with defaults
    } finally {
      setIsLoadingConfig(false);
    }
  }, [project, apiKey]);

  const updateApiKey = async () => {
    if (!project || !apiKey) return;
    
    setIsUpdatingApiKey(true);
    try {
      const response = await fetch(API_ENDPOINTS.PROJECT_CONFIG_APIKEY(project.id), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey
        },
        body: JSON.stringify({
          geminiApiKey: configForm.geminiApiKey || null
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        setProjectConfig(prev => ({
          ...prev,
          hasGeminiApiKey: result.hasGeminiApiKey,
          geminiApiKeyPreview: result.geminiApiKeyPreview
        }));
        // Show the preview in the form after update
        setConfigForm(prev => ({ 
          ...prev, 
          geminiApiKey: result.geminiApiKeyPreview || '' 
        }));
        alert('API key updated successfully!');
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to update API key');
      }
    } catch (error) {
      alert('Error updating API key: ' + error.message);
    } finally {
      setIsUpdatingApiKey(false);
    }
  };

  const updateSystemPrompt = async () => {
    if (!project || !apiKey) return;
    
    setIsUpdatingPrompt(true);
    try {
      const response = await fetch(API_ENDPOINTS.PROJECT_CONFIG_PROMPT(project.id), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey
        },
        body: JSON.stringify({
          systemPrompt: configForm.systemPrompt || getDefaultSystemPrompt()
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        setProjectConfig(prev => ({
          ...prev,
          systemPrompt: result.systemPrompt
        }));
        setConfigForm(prev => ({ ...prev, systemPrompt: result.systemPrompt }));
        alert('System prompt updated successfully!');
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to update system prompt');
      }
    } catch (error) {
      alert('Error updating system prompt: ' + error.message);
    } finally {
      setIsUpdatingPrompt(false);
    }
  };

  const updateAllowedDomains = async () => {
    if (!project || !apiKey) return;
    
    setIsUpdatingDomains(true);
    try {
      // Filter out empty strings when saving (not during typing)
      const cleanedDomains = configForm.allowedDomains.filter(d => d && d.trim());
      
      const response = await fetch(API_ENDPOINTS.PROJECT_CONFIG_DOMAINS(project.id), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey
        },
        body: JSON.stringify({
          allowedDomains: cleanedDomains
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        setProjectConfig(prev => ({
          ...prev,
          allowedDomains: result.allowedDomains || []
        }));
        setConfigForm(prev => ({ ...prev, allowedDomains: result.allowedDomains || [] }));
        alert('Allowed domains updated successfully!');
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to update allowed domains');
      }
    } catch (error) {
      alert('Error updating allowed domains: ' + error.message);
    } finally {
      setIsUpdatingDomains(false);
    }
  };

  const deleteFile = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;
    
    try {
      const response = await fetch(API_ENDPOINTS.FILE_DELETE(project.id, fileId), {
        method: 'DELETE',
        headers: {
          'x-api-key': apiKey
        }
      });
      
      if (response.ok) {
        loadProjectFiles(); // Refresh the file list
        alert('File deleted successfully!');
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to delete file');
      }
    } catch (error) {
      alert('Error deleting file: ' + error.message);
    }
  };

  const reEmbedFiles = async () => {
    if (!files.length) {
      alert('No files to embed. Please upload some files first.');
      return;
    }
    
    if (!window.confirm('This will regenerate embeddings for all files. Continue?')) return;
    
    setIsEmbedding(true);
    try {
      const response = await fetch(API_ENDPOINTS.EMBED(project.id), {
        method: 'POST',
        headers: {
          'x-api-key': apiKey
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        alert('Files re-embedded successfully! Your AI assistant has been updated.');
      } else {
        alert(data.error || 'Failed to re-embed files');
      }
    } catch (error) {
      alert('Error re-embedding files: ' + error.message);
    } finally {
      setIsEmbedding(false);
    }
  };

  const loadApiKeysForProject = useCallback(async () => {
    if (!user || !project) return;
    
    try {
      const response = await fetch(API_ENDPOINTS.API_KEYS(user.uid));
      
      if (response.ok) {
        const data = await response.json();
        // With the new system, we have one API key per user
        if (data.apiKey && !widgetLoaded && project) {
          injectWidget(data.apiKey, project.id);
        }
      }
    } catch (error) {
      console.error("Error loading API key:", error);
    }
  }, [user, project, widgetLoaded]);

  // Load API keys for this project and inject widget
  useEffect(() => {
    if (user && project) {
      loadApiKeysForProject();
    }
  }, [user, project, loadApiKeysForProject]);

  const injectWidget = (apiKey, currentProjectId) => {
    // Set global variables for widget
    window.SWC_API_KEY = apiKey;
    window.SWC_PROJECT_ID = currentProjectId;
    
    // Remove existing widget elements if any
    const existingWidget = document.getElementById('ai-chat-widget');
    if (existingWidget) existingWidget.remove();
    
    const existingToggle = document.getElementById('ai-chat-toggle');
    if (existingToggle) existingToggle.remove();
    
    // Remove existing widget script if any
    const existingScript = document.querySelector('script[src*="widget.js"]');
    if (existingScript) existingScript.remove();
    
    // Create and load the widget script
    const script = document.createElement('script');
    script.src = API_ENDPOINTS.WIDGET_SCRIPT;
    
    script.onload = () => {
      setWidgetLoaded(true);
    };
    
    document.body.appendChild(script);
  };

  const goBack = () => {
    // Clear the widget when leaving
    delete window.SWC_API_KEY;
    delete window.SWC_PROJECT_ID;
    
    // Remove the widget script and widget DOM elements
    const widgetScript = document.querySelector('script[src*="widget.js"]');
    if (widgetScript) {
      widgetScript.remove();
    }
    
    // Remove widget DOM elements
    const widgetElement = document.getElementById('ai-chat-widget');
    if (widgetElement) {
      widgetElement.remove();
    }
    
    const toggleButton = document.getElementById('ai-chat-toggle');
    if (toggleButton) {
      toggleButton.remove();
    }
    
    navigate("/dashboard");
  };

  const goToUpload = () => {
    navigate("/upload", { state: { projectId: project.id } });
  };

  if (!user || !project) {
    return (
      <div className="page-loading">
        <div className="loading-content">
          <div className="loading-logo">AI</div>
          <div className="loading-xl" style={{ margin: '1rem 0' }}></div>
          <h2>Loading Project</h2>
          <p className="loading-text">Initializing your AI assistant...</p>
        </div>
      </div>
    );
  }

  // Show big loading spinner during initial load
  if (isInitialLoading) {
    return (
      <div className="page-loading">
        <div className="loading-content">
          <div className="loading-logo">
            {project?.name ? project.name.substring(0, 2).toUpperCase() : 'AI'}
          </div>
          <div className="loading-xl" style={{ margin: '1rem 0' }}></div>
          <h2>Setting Up {project?.name || 'Project'}</h2>
          <p className="loading-text">Configuring your AI assistant and loading project data...</p>
          <div className="loading-steps">
            <div className={`loading-step ${user && project ? 'completed' : 'loading'}`}>
              <div className="step-icon">✓</div>
              <span>Project Data</span>
            </div>
            <div className={`loading-step ${!user || !project ? 'pending' : !apiKey ? 'loading' : 'completed'}`}>
              <div className="step-icon">✓</div>
              <span>Authentication</span>
            </div>
            <div className={`loading-step ${!user || !project || !apiKey ? 'pending' : isLoadingFiles ? 'loading' : 'completed'}`}>
              <div className="step-icon">✓</div>
              <span>Files & Data</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Loading overlay for operations */}
      {(isUpdatingConfig || isEmbedding || isUpdatingApiKey || isUpdatingPrompt || isUpdatingDomains) && (
        <div className="screen-overlay">
          <div className="loading loading-xl" style={{ marginBottom: '1.25rem' }}></div>
          <h3>
            {isUpdatingConfig ? 'Updating Configuration...' : 
             isUpdatingApiKey ? 'Updating API Key...' :
             isUpdatingPrompt ? 'Updating System Prompt...' :
             isUpdatingDomains ? 'Updating Allowed Domains...' :
             'Re-embedding Files...'}
          </h3>
          <p className="small-text muted-text">
            {isEmbedding ? 'Processing your documents for AI assistance' : 'Saving your configuration securely'}
          </p>
        </div>
      )}

      <div className="project-view">
        <div className="project-breadcrumb">
          <span className="breadcrumb-link" onClick={goBack}>Dashboard</span>
          <span> / </span>
          <span>{project.name || project.id}</span>
        </div>

        <div className="project-header">
          <div>
            <h1>{project.name || project.id}</h1>
          </div>
        </div>

        <div className="project-grid">
          <div className="card">
            <h3>Project Details</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span className="muted-text small-text">Project ID:</span>
                <strong>{project.id}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span className="muted-text small-text">Project Name:</span>
                <strong>{project.name}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span className="muted-text small-text">Created:</span>
                <strong>{formatDate(project.createdAt)}</strong>
              </div>
            </div>
          </div>

          <div className="card">
            <h3>Quick Actions</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <button onClick={goToUpload} className="btn-primary">Upload Files</button>
              <button 
                onClick={() => { navigator.clipboard.writeText(project.id); alert('Project ID copied to clipboard!'); }}
                className="btn-secondary"
              >
                Copy Project ID
              </button>
              <button onClick={goBack} className="btn-secondary">Back to Dashboard</button>
            </div>
          </div>
        </div>

        <div className="card">
          <h3>Data Management</h3>
          <div className="flex-between" style={{ marginBottom: '1rem' }}>
            <p className="small-text muted-text" style={{ margin: 0 }}>Manage uploaded files and regenerate embeddings.</p>
            <button onClick={reEmbedFiles} className="btn-secondary" disabled={isEmbedding || !files.length}
              style={{ opacity: isEmbedding || !files.length ? 0.5 : 1, cursor: isEmbedding || !files.length ? 'not-allowed' : 'pointer' }}>
              {isEmbedding ? 'Re-embedding...' : 'Re-embed All Files'}
            </button>
          </div>

          {files.length > 0 ? (
            <div className="card" style={{ background: 'transparent', padding: '1rem', borderStyle: 'dashed' }}>
              <h4 style={{ margin: '0 0 1rem 0', color: 'rgba(255, 255, 255, 0.9)' }}>Uploaded Files ({files.length})</h4>
              {files.map((file, index) => (
                <div key={file.id || index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0', borderBottom: index < files.length - 1 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none' }}>
                  <div>
                    <div style={{ fontWeight: 'bold' }}>{file.originalName || file.name || file.filename || 'Unknown file'}</div>
                    <div className="small-text muted-text">{file.size ? Math.round(file.size / 1024) : '0'} KB • Uploaded {formatDate(file.uploadedAt)}</div>
                  </div>
                  <button onClick={() => deleteFile(file.id)} className="btn-danger btn-small">Delete</button>
                </div>
              ))}
            </div>
          ) : (
            <div className="upload-area" style={{ textAlign: 'center' }}>
              <p className="small-text muted-text" style={{ margin: 0 }}>No files uploaded yet. Use the "Upload Files" button to add files.</p>
            </div>
          )}
        </div>

        <div className="card">
          <h3>AI Configuration</h3>
          <div className="flex-between" style={{ marginBottom: '1rem' }}>
            <p className="small-text muted-text" style={{ margin: 0 }}>Configure your Gemini API key, system prompt, and security settings for this project.</p>
            <button onClick={() => setShowConfigForm(!showConfigForm)} className="btn-secondary">{showConfigForm ? 'Cancel' : 'Configure AI'}</button>
          </div>

          <div className="status info" style={{ textAlign: 'left' }}>
            <div className="flex-between" style={{ marginBottom: '0.5rem' }}>
              <span>Gemini API Key:</span>
              <span style={{ color: projectConfig.hasGeminiApiKey ? '#22c55e' : '#ef4444' }}>{projectConfig.hasGeminiApiKey ? 'Configured' : 'Not Set'}</span>
            </div>
            {projectConfig.geminiApiKeyPreview && (
              <div className="small-text muted-text" style={{ marginBottom: '0.5rem' }}>Preview: {projectConfig.geminiApiKeyPreview}</div>
            )}
            <div className="flex-between" style={{ marginBottom: '0.5rem' }}>
              <span>System Prompt:</span>
              <span style={{ color: projectConfig.systemPrompt ? '#22c55e' : '#f59e0b' }}>{projectConfig.systemPrompt ? 'Custom' : 'Default'}</span>
            </div>
            <div className="flex-between">
              <span>Allowed Domains:</span>
              <span style={{ color: projectConfig.allowedDomains && projectConfig.allowedDomains.length > 0 ? '#22c55e' : '#f59e0b' }}>
                {projectConfig.allowedDomains && projectConfig.allowedDomains.length > 0 ? `${projectConfig.allowedDomains.length} domain(s)` : 'All domains'}
              </span>
            </div>
            {projectConfig.allowedDomains && projectConfig.allowedDomains.length > 0 && (
              <div className="small-text muted-text" style={{ marginTop: '0.5rem' }}>Domains: {projectConfig.allowedDomains.join(', ')}</div>
            )}
          </div>

          {showConfigForm && (
            <div className="card" style={{ background: 'transparent', borderStyle: 'dashed' }}>
              {/* API Key Section */}
              <div className="card" style={{ background: 'transparent' }}>
                <h4 style={{ color: '#00ffff', margin: '0 0 1rem 0', fontSize: '1rem' }}>Gemini API Key</h4>
                <div style={{ marginBottom: '1rem' }}>
                  <input type="password" value={configForm.geminiApiKey} onChange={(e) => setConfigForm({...configForm, geminiApiKey: e.target.value})} placeholder="Enter your Gemini API key (AIza...)" />
                  <div className="small-text muted-text" style={{ marginTop: '0.5rem' }}>
                    Get your API key from <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" style={{ color: '#00ffff' }}>Google AI Studio</a>
                  </div>
                </div>
                <button onClick={updateApiKey} className="btn-primary btn-small" disabled={isUpdatingApiKey || !configForm.geminiApiKey.trim()}>
                  {isUpdatingApiKey ? 'Updating API Key...' : 'Update API Key'}
                </button>
              </div>

              {/* System Prompt Section */}
              <div className="card" style={{ background: 'transparent' }}>
                <h4 style={{ color: '#00ffff', margin: '0 0 1rem 0', fontSize: '1rem' }}>System Prompt</h4>
                <div style={{ marginBottom: '1rem' }}>
                  <textarea value={configForm.systemPrompt} onChange={(e) => setConfigForm({...configForm, systemPrompt: e.target.value})} placeholder={getDefaultSystemPrompt()} rows={6} style={{ width: '100%', resize: 'vertical' }} />
                  <div className="small-text muted-text" style={{ marginTop: '0.5rem' }}>Define the personality, tone, and behavior of your AI assistant</div>
                </div>
                <button onClick={updateSystemPrompt} className="btn-primary btn-small" disabled={isUpdatingPrompt}>
                  {isUpdatingPrompt ? 'Updating Prompt...' : 'Update System Prompt'}
                </button>
              </div>

              {/* Allowed Domains Section */}
              <div className="card" style={{ background: 'transparent' }}>
                <h4 style={{ color: '#00ffff', margin: '0 0 1rem 0', fontSize: '1rem' }}>Domain Security</h4>
                <div style={{ marginBottom: '1rem' }}>
                  <input type="text" value={Array.isArray(configForm.allowedDomains) ? configForm.allowedDomains.join(', ') : ''} onChange={(e) => { const domains = e.target.value.split(',').map(d => d.trim()); setConfigForm({...configForm, allowedDomains: domains}); }} placeholder="example.com, mydomain.org (comma-separated)" />
                  <div className="small-text muted-text" style={{ marginTop: '0.5rem' }}>
                    Only these domains will be able to load your widget. Leave empty to allow all domains.
                  </div>
                </div>
                <button onClick={updateAllowedDomains} className="btn-primary btn-small" disabled={isUpdatingDomains}>
                  {isUpdatingDomains ? 'Updating Domains...' : 'Update Allowed Domains'}
                </button>
              </div>

              <div className="flex-between" style={{ paddingTop: '1rem', borderTop: '1px solid #1f2332' }}>
                <div></div>
                <button onClick={() => setShowConfigForm(false)} className="btn-secondary">Close</button>
              </div>
            </div>
          )}
        </div>

        <div className="card">
          <h3>Widget Integration</h3>
          <p className="small-text muted-text" style={{ marginBottom: '1.5rem' }}>AI chat widget status for this project.</p>

          {!projectConfig.hasGeminiApiKey ? (
            <div className="status error" style={{ textAlign: 'left' }}>
              <strong>API KEY REQUIRED</strong>
              <p className="small-text muted-text" style={{ margin: '0.5rem 0 0 0' }}>Please configure your Gemini API key to enable the widget.</p>
            </div>
          ) : widgetLoaded ? (
            <div className="status success" style={{ textAlign: 'left' }}>
              <strong>WIDGET ACTIVE</strong>
              <p className="small-text muted-text" style={{ margin: '0.5rem 0 0 0' }}>Look for the chat bubble in the bottom-right corner!</p>
            </div>
          ) : (
            <div className="status info" style={{ textAlign: 'left' }}>
              <strong>WIDGET LOADING</strong>
              <p className="small-text muted-text" style={{ margin: '0.5rem 0 0 0' }}>Setting up your AI assistant...</p>
            </div>
          )}

          {projectConfig.hasGeminiApiKey && !widgetLoaded && (
            <button onClick={() => injectWidget(apiKey, project.id)} className="btn-primary btn-small" style={{ marginTop: '0.75rem' }}>
              Load Widget Manually
            </button>
          )}

          {projectConfig.allowedDomains && projectConfig.allowedDomains.length > 0 && (
            <div className="status success" style={{ textAlign: 'left', background: 'rgba(34, 197, 94, 0.05)' }}>
              <strong>Domain Security Active</strong>
              <p className="small-text muted-text" style={{ margin: '0.5rem 0 0 0' }}>Your widget will only load on: {projectConfig.allowedDomains.join(', ')}</p>
            </div>
          )}

          {(!projectConfig.allowedDomains || projectConfig.allowedDomains.length === 0) && projectConfig.hasGeminiApiKey && (
            <div className="status info" style={{ textAlign: 'left', background: 'rgba(245, 158, 11, 0.05)', borderColor: '#f59e0b' }}>
              <strong>Security Notice</strong>
              <p className="small-text muted-text" style={{ margin: '0.5rem 0 0 0' }}>No domain restrictions are set. Anyone can use your widget on any website. Consider adding allowed domains for better security.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProjectView;
